import { Injectable } from "@angular/core";

import { TranslateService } from "@ngx-translate/core";

import { Store } from "@ngrx/store";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { from } from "rxjs";
import {
  exhaustMap,
  map,
  catchError,
  tap,
  switchMap,
  mergeMap,
} from "rxjs/operators";

import { AppState } from "../../reducers";
import * as VehiclesActionTypes from "../../actions/vehicles/vehicles.actions";
import * as MessagesActionTypes from "../../actions/messages/message.actions";

import { VehiclesService } from "src/app/shared/services/vehicles/vehicles.service";
import { CategoriesService } from "src/app/shared/services/categories/categories.service";
import { PropertiesService } from "src/app/shared/services/properties/properties.service";
import { PartiesService } from "src/app/shared/services/parties/parties.service";
import { ProductGroupService } from "src/app/shared/services/products/productGroup/productGroup.service";
import { CardsService } from "src/app/shared/services/cards/cards.service";
import { DashboardService } from "src/app/shared/services/dashboard/dashboard.service";
import { MovementService } from "src/app/shared/services/movements/movement.service";

import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { TableConfigService } from "src/app/shared/services/table-config/table-config.service";

@Injectable()
export class VehiclesEffects {
  // Create Vehicle
  createVehicle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActionTypes.createVehicle),
      exhaustMap((action) =>
        this.vehiclesService.createVehicle(action).pipe(
          mergeMap((result) => [
            VehiclesActionTypes.resetCreation(),
            VehiclesActionTypes.getVehicle({
              vehicleCode: result?.result?.code,
            })
          ]),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Vehicles.vehicleCreated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              VehiclesActionTypes.setLoadingVehiclesEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "Vehicles.vehicleCreateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Update Vehicle
  updateVehicle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActionTypes.updateVehicle),
      exhaustMap((action) =>
        this.vehiclesService.updateVehicle(action).pipe(
          map((result) => {
            return VehiclesActionTypes.getVehicle({
              vehicleCode: result?.result?.code,
            });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Vehicles.vehicleUpdated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              VehiclesActionTypes.setLoadingVehiclesEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "Vehicles.vehicleUpdateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Get Vehicle
  getVehicle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActionTypes.getVehicle),
      exhaustMap((action) =>
        this.vehiclesService.getVehicle(action).pipe(
          map((result) => VehiclesActionTypes.setVehicle({ vehicle: result })),
          catchError((error) => {
            const actions = [
              VehiclesActionTypes.setLoadingVehiclesEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Vehicles.VehicleLoadingError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Get Vehicle List
  getVehicleList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActionTypes.getVehicleList),
      switchMap((action) =>
        this.vehiclesService
          .getVehiclesList(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              VehiclesActionTypes.loadVehicleList({ response: result })
            ),
            catchError((error) => {
              const actions = [
                VehiclesActionTypes.loadVehicleList({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Vehicles.loadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  getVehicleExcelList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActionTypes.getVehicleExcelList),
      switchMap((action) =>
        this.vehiclesService
          .getVehiclesList(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              VehiclesActionTypes.loadVehicleExcelList({ response: result })
            ),
            catchError((error) => {
              const actions = [
                VehiclesActionTypes.loadVehicleExcelList({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Vehicles.loadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Get Vehicles Table Config
  getVehicleTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActionTypes.getVehicleTableConfig),
      exhaustMap((action) =>
        this.tableConfigService
          .getConfigSettings(action.options.code, action.options.settingType)
          .pipe(
            map((result) =>
              VehiclesActionTypes.loadVehicleTableConfig({ response: result })
            ),
            catchError((error) => {
              const actions = [
                VehiclesActionTypes.loadVehicleTableConfig({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "TableConfig.LoadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Save Vehicles Table Config
  saveVehicleTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActionTypes.saveVehicleTableConfig),
      exhaustMap((action) =>
        this.tableConfigService.saveConfigSettings(action.config).pipe(
          mergeMap(() => [
            VehiclesActionTypes.setLoadingVehiclesEndState(),
            MessagesActionTypes.setSnackbar(
              this.buildSnackbarObj(
                "TableConfig.SaveSuccess",
                SnackbarType.SUCCESS
              )
            ),
          ]),
          catchError((error: any) => {
            const actions = [
              VehiclesActionTypes.setLoadingVehiclesEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "TableConfig.SaveError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  //get Cards by Vehicle
  getVehicleCardsList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActionTypes.getByVehicleCardsList),
      exhaustMap((action) =>
        this.cardsService
          .getFiltredCardList(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.container
          )
          .pipe(
            map((result) =>
              VehiclesActionTypes.loadByVehicleCardsList({ response: result })
            ),
            catchError((error) => {
              const actions = [
                VehiclesActionTypes.loadByVehicleCardsList({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Vehicles.loadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Get Categories by Usage
  getCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActionTypes.getVehicleCategoriesByUsage),
      exhaustMap((action) =>
        this.categoriesService
          .getCategoriesByUsage(action.pageReq, action.usageCode)
          .pipe(
            map((result) =>
              VehiclesActionTypes.loadVehicleCategoriesByUsage({
                response: result,
              })
            ),
            catchError((error) => {
              const actions = [
                VehiclesActionTypes.setLoadingVehiclesEndState(),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "VehiclesApp.ErrorLoadCategories",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Get Optional Categories by Usage
  getOptionalCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActionTypes.getVehicleOptionalCategoriesByUsage),
      exhaustMap((action) =>
        this.categoriesService
          .getCategoriesByUsage(action.pageReq, action.usageCode)
          .pipe(
            map((result) =>
              VehiclesActionTypes.loadVehicleOptionalCategoriesByUsage({
                response: result,
              })
            ),
            catchError((error) => {
              const actions = [
                VehiclesActionTypes.setLoadingVehiclesEndState(),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj("App.Errors.Error", SnackbarType.ERROR)
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Get Properties by Usage
  getProperties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActionTypes.getVehiclePropertiesByUsage),
      exhaustMap((action) =>
        this.propertiesService
          .getPropertiesByUsage(action.pageReq, action.usageCode)
          .pipe(
            map((result) =>
              VehiclesActionTypes.loadVehiclePropertiesByUsage({
                response: result,
              })
            ),
            catchError((error) => {
              const actions = [
                VehiclesActionTypes.setLoadingVehiclesEndState(),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Vehicles.errorLoadProperties",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Get Optional Properties by Usage
  getOptionalProperties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActionTypes.getVehicleOptionalPropertiesByUsage),
      exhaustMap((action) =>
        this.propertiesService
          .getPropertiesByUsage(action.pageReq, action.usageCode)
          .pipe(
            map((result) =>
              VehiclesActionTypes.loadVehicleOptionalPropertiesByUsage({
                response: result,
              })
            ),
            catchError((error) => {
              const actions = [
                VehiclesActionTypes.setLoadingVehiclesEndState(),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj("App.Errors.Error", SnackbarType.ERROR)
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Get Categories for the list by Usage
  getCategoriesForList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActionTypes.getVehicleCategoriesForListByUsage),
      exhaustMap((action) =>
        this.categoriesService
          .getCategoriesByUsage(action.pageReq, action.usageCode)
          .pipe(
            map((result) =>
              VehiclesActionTypes.loadVehicleCategoriesForListByUsage({
                response: result,
              })
            ),
            catchError((error) => {
              const actions = [
                VehiclesActionTypes.setLoadingVehiclesEndState(),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Vehicles.ErrorLoadCategories",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Get Properties for the list by Usage
  getPropertiesForList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActionTypes.getVehiclePropertiesForListByUsage),
      exhaustMap((action) =>
        this.propertiesService
          .getPropertiesByUsage(action.pageReq, action.usageCode)
          .pipe(
            map((result) =>
              VehiclesActionTypes.loadVehiclePropertiesForListByUsage({
                response: result,
              })
            ),
            catchError((error) => {
              const actions = [
                VehiclesActionTypes.setLoadingVehiclesEndState(),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Vehicles.errorLoadProperties",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Get Vehicle Brand
  getBrands$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActionTypes.getBrands),
      exhaustMap((action) =>
        this.vehiclesService.getBrands(action.pageReq).pipe(
          map((result) => VehiclesActionTypes.loadBrands({ response: result })),
          catchError((error) => {
            const actions = [
              VehiclesActionTypes.setLoadingVehiclesEndState,
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Vehicles.errorLoadBrands",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Get Vehicle Model
  getModels$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActionTypes.getModels),
      exhaustMap((action) =>
        this.vehiclesService.getModels(action.pageReq).pipe(
          map((result) => VehiclesActionTypes.loadModels({ response: result })),
          catchError((error) => {
            const actions = [
              VehiclesActionTypes.setLoadingVehiclesEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Vehicles.errorLoadModels",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Get Vehicle Version
  getVersions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActionTypes.getVersions),
      exhaustMap((action) =>
        this.vehiclesService.getVersions(action.pageReq).pipe(
          map((result) =>
            VehiclesActionTypes.loadVersions({ response: result })
          ),
          catchError((error) => {
            const actions = [
              VehiclesActionTypes.setLoadingVehiclesEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Vehicles.errorLoadVersion",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Get Vehicle Motorization
  getMotorizations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActionTypes.getMotorizations),
      exhaustMap((action) =>
        this.vehiclesService.getMotorizations(action.pageReq).pipe(
          map((result) =>
            VehiclesActionTypes.loadMotorizations({ response: result })
          ),
          catchError((error) => {
            const actions = [
              VehiclesActionTypes.setLoadingVehiclesEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Vehicles.errorLoadMotorization",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Get Customer Vehicle List
  getCustomerVehicleList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActionTypes.getCustomerVehicleList),
      exhaustMap((action) =>
        this.partiesService
          .getCustomerPartyList(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs
          )
          .pipe(
            map((result) =>
              VehiclesActionTypes.loadCustomerVehicleList({ response: result })
            ),
            catchError((error) => {
              const actions = [
                VehiclesActionTypes.setLoadingVehiclesEndState(),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Vehicles.loadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  getVehicleTransactionsList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActionTypes.getByTransactionsVehicleList),
      exhaustMap((action) =>
        this.movementsService
          .getByVehicleTransactionsList(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs
          )
          .pipe(
            map((result) =>
              VehiclesActionTypes.loadByTransactionsVehicleList({
                response: result,
              })
            ),
            catchError((error) => {
              const actions = [
                VehiclesActionTypes.loadByTransactionsVehicleList({
                  response: [],
                }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Vehicles.loadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Get Vehicles Product Groups
  getProductGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActionTypes.getProductGroups),
      exhaustMap((action) =>
        this.productGroupService.getProductGroup(action.pageReq).pipe(
          map((result) =>
            VehiclesActionTypes.loadProductGroups({ response: result })
          ),
          catchError((error) => {
            const actions = [
              VehiclesActionTypes.setLoadingVehiclesEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Vehicles.errorLoadProductGroup",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Save Vehicle Tanks
  saveVehicleTanks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActionTypes.saveVehicleTanks),
      exhaustMap((action) =>
        this.vehiclesService.saveVehicleTanks(action).pipe(
          map((result) => VehiclesActionTypes.loadVehicles(null)),
          catchError((error) => {
            const actions = [
              VehiclesActionTypes.setLoadingVehiclesEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Vehicles.vehicleTankCreateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Delete Vehicle Tanks
  deleteVehicleTanks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActionTypes.deleteVehicleTanks),
      exhaustMap((action) =>
        this.vehiclesService.deleteVehicleTanks(action).pipe(
          map((result) => VehiclesActionTypes.loadVehicles(null)),
          catchError((error) => {
            const actions = [
              VehiclesActionTypes.setLoadingVehiclesEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Vehicles.vehicleTanksDeleteFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Get Vehicles Odometer Types
  getOdometerTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActionTypes.getOdometerTypeList),
      exhaustMap((action) =>
        this.vehiclesService.getOdometerTypes(action.pageReq).pipe(
          map((result) =>
            VehiclesActionTypes.loadOdometerTypeVehicleList({
              response: result,
            })
          ),
          catchError((error) => {
            const actions = [
              VehiclesActionTypes.setLoadingVehiclesEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Vehicles.errorLoadOdometer",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Get Vehicles Consumption Rules
  getConsumptionRules$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActionTypes.getConsumptionRuleList),
      exhaustMap((action) =>
        this.vehiclesService.getConsumptionRules(action.pageReq).pipe(
          map((result) =>
            VehiclesActionTypes.loadConsumptionRuleVehicleList({
              response: result,
            })
          ),
          catchError((error) => {
            const actions = [
              VehiclesActionTypes.setLoadingVehiclesEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Vehicles.errorLoadConsumptionRules",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Save Vehicle Odometers
  saveVehicleODOs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActionTypes.saveVehicleODOs),
      exhaustMap((action) =>
        this.vehiclesService.saveVehicleODOs(action).pipe(
          map((result) => VehiclesActionTypes.loadVehicles(null)),
          catchError((error) => {
            const actions = [
              VehiclesActionTypes.setLoadingVehiclesEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Vehicles.vehicleODOCreateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Delete Vehicle Odometers
  deleteVehicleODOs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActionTypes.deleteVehicleODOs),
      exhaustMap((action) =>
        this.vehiclesService.deleteVehicleODOs(action).pipe(
          map((result) => VehiclesActionTypes.loadVehicles(null)),
          catchError((error) => {
            const actions = [
              VehiclesActionTypes.setLoadingVehiclesEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Vehicles.vehicleODOsDeleteFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Save Vehicle Averages
  saveVehicleAverages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActionTypes.saveVehicleAverages),
      exhaustMap((action) =>
        this.vehiclesService.saveVehicleAverages(action).pipe(
          map((result) => VehiclesActionTypes.loadVehicles(null)),
          catchError((error) => {
            const actions = [
              VehiclesActionTypes.setLoadingVehiclesEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Vehicles.vehicleAverageCreateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Delete Vehicle Averages
  deleteVehicleAverages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActionTypes.deleteVehicleAverages),
      exhaustMap((action) =>
        this.vehiclesService.deleteVehicleAverages(action).pipe(
          map((result) => VehiclesActionTypes.loadVehicles(null)),
          catchError((error) => {
            const actions = [
              VehiclesActionTypes.setLoadingVehiclesEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Vehicles.vehicleAverageDeleteFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Get Vehicle Averages
  getVehicleAverages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VehiclesActionTypes.getVehicleAverages),
      exhaustMap((action) =>
        this.dashBoardService.getVehicleAverages(action?.vehicleCode).pipe(
          map((result) =>
            VehiclesActionTypes.loadVehicleAverages({ response: result })
          ),
          catchError((error) => {
            const actions = [
              VehiclesActionTypes.setLoadingVehiclesEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Vehicles.VehicleAveragesLoadingError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private vehiclesService: VehiclesService,
    private categoriesService: CategoriesService,
    private propertiesService: PropertiesService,
    private translate: TranslateService,
    private partiesService: PartiesService,
    private productGroupService: ProductGroupService,
    private cardsService: CardsService,
    private dashBoardService: DashboardService,
    private movementsService: MovementService,
    private tableConfigService: TableConfigService
  ) {}

  buildSnackbarObj(message: string, snackbarType: SnackbarType) {
    return {
      snackbar: {
        message: this.translate.instant(message),
        type: snackbarType,
      },
    };
  }
}
